import React from "react";

import { styled } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { LoadingButton } from "@atlaskit/button";
import DropdownMenu, {
    DropdownItem,
    DropdownItemGroup,
    CustomTriggerProps
} from "@atlaskit/dropdown-menu";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import InboxIcon from "@atlaskit/icon/core/inbox";
import OfficeBuildingIcon from "@atlaskit/icon/core/office-building";
import { Inline, Text } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { useFireAnalyticsEvents } from "../../../../../analytics";
import {
    SourceKey,
    ActionKey,
    ActionSubjectKey,
    ActionSubjectIdKey
} from "../../../../../analytics/constants";
import {
    useFastshiftDispatch,
    useFastshiftSelector,
    NO_ORG_ID
} from "../../../../state";
import { setSelectedOrg } from "../../../../state/actions";

import { messages } from "./messages";

// hacky approach - styles dropdown button to match design requirements
// can be replaced with <Pressable/> once @atlaskit/primitives package is updated
const CustomStyledDropdownButton = styled.div({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        "borderRadius": `${token("space.100")}`,
        "backgroundColor": `${token("color.text")}`,
        "display": "flex",
        "alignItems": "center",
        "padding": `${token("space.050")} ${token("space.100")}`,
        "&[aria-expanded='true']": {
            backgroundColor: `${token("color.background.selected")}`
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "&[disabled] p": {
            color: `${token("color.text.disabled")}`
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "&[disabled] svg": {
            color: `${token("color.text.disabled")}`
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "&[aria-expanded='true'] p": {
            color: `${token("color.text")}`
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "p": {
            color: `${token("color.text.inverse")}`
        }
    }
});

const OrgSelectorButtonMessage = () => {
    const selectedOrg = useFastshiftSelector((state) => state.selectedOrg);
    const orgs = useFastshiftSelector((state) => state.orgs);
    const isOrgAdmin = orgs.data.length > 1;

    if (orgs?.pending) {
        return (
            <FormattedMessage {...messages.orgSelectorDropdownLoadingOrgs} />
        );
    }

    if (!isOrgAdmin) {
        return (
            <FormattedMessage {...messages.orgSelectorDropdownNotOrgAdmin} />
        );
    }

    if (isOrgAdmin && selectedOrg?.id === NO_ORG_ID) {
        return (
            <FormattedMessage
                {...messages.orgSelectorDropdownNoOrgForOrgAdmin}
            />
        );
    }

    return (
        <FormattedMessage
            {...messages.orgSelectorDropdownSelectedCloudOrg}
            values={{ orgName: selectedOrg?.name }}
        />
    );
};

const OrgSelectorDropdownButton = ({
    triggerRef,
    ...providedProps
}: CustomTriggerProps) => {
    const selectedOrg = useFastshiftSelector((state) => state.selectedOrg);
    const orgs = useFastshiftSelector((state) => state.orgs);
    const isOrgAdmin = orgs.data.length > 1;
    const { fireUIEvent } = useFireAnalyticsEvents();

    const sendClickDropdownEvent = () => {
        fireUIEvent({
            action: ActionKey.CLICKED,
            actionSubject: ActionSubjectKey.DROPDOWN,
            actionSubjectId: ActionSubjectIdKey.ORG_SELECTOR_DROPDOWN,
            source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
            attributes: {}
        });
    };

    return (
        <CustomStyledDropdownButton>
            <LoadingButton
                ref={triggerRef}
                {...providedProps}
                appearance="primary"
                spacing="compact"
                isDisabled={!selectedOrg || !isOrgAdmin}
                isLoading={!orgs || orgs?.pending}
                onClick={(e) => {
                    sendClickDropdownEvent();
                    providedProps.onClick?.(e);
                }}
            >
                <Inline
                    space="space.100"
                    alignInline="center"
                    alignBlock="center"
                >
                    {selectedOrg?.id === NO_ORG_ID ? (
                        <InboxIcon
                            label="Unassociated icon"
                            color={token("color.icon.accent.teal")}
                        />
                    ) : (
                        <OfficeBuildingIcon
                            label="Org icon"
                            color={token("color.icon.accent.teal")}
                        />
                    )}
                    <Text>
                        <OrgSelectorButtonMessage />
                    </Text>
                    <ChevronDownIcon label="Chevron down icon" size="small" />
                </Inline>
            </LoadingButton>
        </CustomStyledDropdownButton>
    );
};

export const OrgSelectorDropdown: React.FC = () => {
    const selectedOrg = useFastshiftSelector((state) => state.selectedOrg);
    const orgs = useFastshiftSelector((state) => state.orgs);
    const dispatch = useFastshiftDispatch();
    const isOrgAdmin = orgs.data.length > 1;
    const { fireUIEvent, fireTrackEvent } = useFireAnalyticsEvents();

    // send track event once org selector dropdown is loaded
    const loadStartRef = React.useRef<number>(Date.now());
    const hasSentTrackEvent = React.useRef<boolean>(false);
    React.useEffect(() => {
        const orgsLoaded =
            orgs && orgs.data && orgs.data.length > 0 && !orgs.pending;
        const selectedOrgLoaded = !!selectedOrg;
        if (orgsLoaded && selectedOrgLoaded && !hasSentTrackEvent.current) {
            const elapsed = Date.now() - loadStartRef.current;
            hasSentTrackEvent.current = true;

            fireTrackEvent({
                action: ActionKey.DISPLAYED,
                actionSubject: ActionSubjectKey.DROPDOWN,
                actionSubjectId: ActionSubjectIdKey.ORG_SELECTOR_DROPDOWN,
                source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
                attributes: {
                    orgDropdownEnabled: isOrgAdmin,
                    loadTime: elapsed,
                    selectedOrgId: selectedOrg.id,
                    totalOrgCount: orgs.data.length - 1 // excluding 'No Org'
                }
            });
        }
    }, [orgs, selectedOrg, isOrgAdmin, fireTrackEvent]);

    // not org admin UI display
    if (!isOrgAdmin && selectedOrg) {
        return (
            <Text>
                <FormattedMessage
                    {...messages.orgSelectorDropdownNotOrgAdmin}
                />
            </Text>
        );
    }

    const sendClickDropdownItemEvent = (orgId: string) => {
        fireUIEvent({
            action: ActionKey.CLICKED,
            actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
            actionSubjectId: ActionSubjectIdKey.ORG_SELECTOR_DROPDOWN,
            source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
            attributes: {
                selectedOrgId: orgId
            }
        });
    };

    return (
        <DropdownMenu<HTMLButtonElement>
            // this is atlaskit's recommended pattern for custom trigger components
            // eslint-disable-next-line react/no-unstable-nested-components
            trigger={({ triggerRef, ...providedProps }) => (
                <OrgSelectorDropdownButton
                    triggerRef={triggerRef}
                    {...providedProps}
                />
            )}
            shouldRenderToParent
        >
            <DropdownItemGroup>
                {orgs.data.map((org) => (
                    <DropdownItem
                        key={org.id}
                        isSelected={org.id === selectedOrg?.id}
                        onClick={() => {
                            dispatch(setSelectedOrg(org));
                            sendClickDropdownItemEvent(org.id);
                        }}
                    >
                        <Inline space="space.100" alignBlock="center">
                            {org.id === NO_ORG_ID ? (
                                <InboxIcon
                                    label="Unassociated icon"
                                    color={token("color.icon.accent.teal")}
                                />
                            ) : (
                                <OfficeBuildingIcon
                                    label="Org icon"
                                    color={token("color.icon.accent.teal")}
                                />
                            )}
                            <Text>
                                {org.id === NO_ORG_ID ? (
                                    <FormattedMessage
                                        {...messages.orgSelectorDropdownNoOrgForOrgAdmin}
                                    />
                                ) : (
                                    org?.name
                                )}
                            </Text>
                        </Inline>
                    </DropdownItem>
                ))}
            </DropdownItemGroup>
        </DropdownMenu>
    );
};
